import Button from "../../components/ui/Button";
import EnumBadge from "../../components/ui/EnumBadge";
import CompactReference from "../../components/ui/CompactReference";
import { ProfileHeader, DetailCard, DetailField } from "../../components/ui/ProfileDetails";
import { FiArrowLeft, FiMail, FiBriefcase, FiEdit2, FiInfo, FiPackage } from "react-icons/fi";
import { toast } from "react-toastify";
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";

import {
  getVendorById,
  updateVendor,
  deleteVendor,
  getApiErrorMessage,
} from "../../services/vendorService";

import { Vendor, SingleVendorApiResponse } from "../../types/vendor";

import EditVendorModal from "../../compoonents/EditVendorModal/EditVendorModal";
import DeleteConfirmationModal from "../../compoonents/DeleteConfirmationModal/DeleteConfirmationModal";
import VendorProductsTab from "../../compoonents/VendorProductsTab/VendorProductsTab";


import "./VendorDetailsPage.css";

const VendorDetailsPage: React.FC = () => {
  const { vendorId } = useParams<{ vendorId: string }>();
  const navigate = useNavigate();

  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<"details" | "products">("details");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchVendorDetails = useCallback(async () => {
    if (!vendorId) {
      setError("No Vendor ID was provided in the URL.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response: SingleVendorApiResponse = await getVendorById(vendorId);

      if (response && response.success) {
        setVendor(response.data);
      } else {
        setError("Could not find the requested vendor.");
      }
    } catch (err) {
      setError("An error occurred while fetching vendor details.");
      console.error("Fetch Vendor Details Error:", err);
    } finally {
      setLoading(false);
    }
  }, [vendorId]);

  useEffect(() => {
    fetchVendorDetails();
  }, [fetchVendorDetails]);

  const handleUpdateVendor = async (updatedData: Partial<Vendor>) => {
    if (!vendorId) return;

    setIsSaving(true);
    try {
      const response = await updateVendor(vendorId, updatedData);
      if (response && response.vendor) {
        setVendor(response.vendor);
        setIsEditModalOpen(false);
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to update vendor. Please try again."));
      console.error("Update Vendor Error:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteVendor = async () => {
    if (!vendorId) return;

    setIsDeleting(true);
    try {
      await deleteVendor(vendorId);
      toast.success("Vendor deleted successfully.");
      navigate("/vendors");
    } catch (err) {
      toast.error("Failed to delete vendor. Please try again.");
      console.error("Delete Vendor Error:", err);
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
    }
  };

  const renderTabContent = () => {
    if (!vendor) return null;

    switch (activeTab) {
      case "details":
        return (
          <div className="navo-detail-grid">
            <DetailCard title="Contact information" description="How to reach this vendor" icon={<FiMail />}>
              <dl className="navo-detail-fields">
                <DetailField label="Email address" wide>{vendor.email || "Not provided"}</DetailField>
                <DetailField label="Phone number" wide>{vendor.phone || "Not provided"}</DetailField>
                <DetailField label="Business address" wide>{vendor.address || "Not provided"}</DetailField>
              </dl>
            </DetailCard>
            <DetailCard title="Business details" description="Vendor identity and account information" icon={<FiBriefcase />}>
              <dl className="navo-detail-fields">
                <DetailField label="Vendor reference"><CompactReference value={vendor.id} /></DetailField>
                <DetailField label="Date joined">{new Date(vendor.createdAt).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}</DetailField>
                <DetailField label="Description" wide>{vendor.description || "No description provided."}</DetailField>
              </dl>
            </DetailCard>
          </div>
        );
      case "products":
        return <VendorProductsTab vendorId={vendor.id} />;
      default:
        return null;
    }
  };

  if (loading)
    return <div className="page-loading">Loading Vendor Details...</div>;
  if (error) return <div className="page-error">{error}</div>;
  if (!vendor) return <div className="page-error">Vendor not found.</div>;

  return (
    <>
      <div className="vendor-details-page">
        <div className="details-header">
          <Button variant="text" startIcon={<FiArrowLeft />} onClick={() => navigate("/vendors")}>All vendors</Button>
        </div>

        <ProfileHeader name={vendor.name} image={vendor.logo} tone="orange" subtitle={<>{vendor.email || "Vendor profile"} · Vendor account</>}
          badge={<EnumBadge value={vendor.isActive ? "active" : "blocked"} label={vendor.isActive ? "Active" : "Inactive"} />}
          actions={<><Button variant="primary" startIcon={<FiEdit2 />} onClick={() => setIsEditModalOpen(true)}>Edit Vendor</Button><Button variant="text" className="vendor-delete-action" onClick={() => setIsDeleteModalOpen(true)}>Delete Vendor</Button></>} />

        <div className="navo-profile-tabs" aria-label="Vendor profile sections">
          <button
            aria-pressed={activeTab === "details"} className={activeTab === "details" ? "active" : ""}
            onClick={() => setActiveTab("details")}
          >
            <FiInfo aria-hidden="true" />Vendor Information
          </button>
          <button
            aria-pressed={activeTab === "products"} className={activeTab === "products" ? "active" : ""}
            onClick={() => setActiveTab("products")}
          >
            <FiPackage aria-hidden="true" />Products
          </button>
        </div>

        <main className="details-content-container">{renderTabContent()}</main>
      </div>

      {isEditModalOpen && (
        <EditVendorModal
          vendor={vendor}
          onClose={() => setIsEditModalOpen(false)}
          onSave={handleUpdateVendor}
          isSaving={isSaving}
        />
      )}

      {isDeleteModalOpen && (
        <DeleteConfirmationModal
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={handleDeleteVendor}
          isDeleting={isDeleting}
          itemName={vendor.name}
        />
      )}
    </>
  );
};

export default VendorDetailsPage;
